import React, { useState, useEffect, useRef } from "react";
import { ChevronDown, ChevronUp, BookOpen } from "lucide-react";
import { MathText } from "../MathText";

export interface PassageData {
  id?: string;
  title?: string | null;
  text?: string;
  body?: string;
}

interface QuestionPassageLayoutProps {
  passage?: PassageData | null;
  children: React.ReactNode;
  className?: string;
  variant?: "single" | "multi";
}

export function QuestionPassageLayout({
  passage,
  children,
  className = "",
  variant = "single",
}: QuestionPassageLayoutProps) {
  const passageText = passage?.text || passage?.body || "";
  const passageTitle = passage?.title?.trim() || "";
  const passageId = passage?.id;

  // Track expanded state for mobile.
  // Requirement: starts expanded the first time a passage appears; afterwards it keeps
  // the state the student left it in while moving between questions that share the same passage.
  const [isExpanded, setIsExpanded] = useState<boolean>(true);
  const prevPassageIdRef = useRef<string | undefined>(undefined);

  useEffect(() => {
    if (passageId && passageId !== prevPassageIdRef.current) {
      // New passage encountered: reset to expanded by default
      setIsExpanded(true);
      prevPassageIdRef.current = passageId;
    }
  }, [passageId]);

  // If no passage is attached or passage text is empty, render children as-is
  if (!passage || !passageText) {
    return <>{children}</>;
  }

  // Render passage content (Markdown + LaTeX)
  const renderPassageBody = () => (
    <section
      id="reading-passage-region"
      aria-label="Reading passage"
      className="p-4 sm:p-5 text-sm leading-relaxed text-foreground space-y-3"
    >
      {passageTitle && (
        <h3 className="text-base sm:text-lg font-bold text-foreground tracking-tight border-b border-border/60 pb-2">
          {passageTitle}
        </h3>
      )}
      <div className="prose prose-sm dark:prose-invert max-w-none">
        <MathText>{passageText}</MathText>
      </div>
    </section>
  );

  if (variant === "multi") {
    // Multi-question mode (e.g. Review / Results page displaying several questions):
    // Show the passage once above its first question and keep it sticky or collapsible.
    return (
      <div className={`space-y-4 ${className}`.trim()}>
        <div className="bg-card border border-border rounded-xl shadow-xs overflow-hidden sticky top-16 z-20">
          <div className="bg-muted/40 px-4 py-2.5 border-b border-border flex items-center justify-between">
            <div className="flex items-center gap-2">
              <BookOpen size={16} className="text-primary" />
              <span className="text-xs font-bold uppercase tracking-wider text-muted-foreground">
                Passage
              </span>
              {passageTitle && (
                <span className="text-xs font-semibold text-foreground truncate max-w-xs">
                  · {passageTitle}
                </span>
              )}
            </div>
            <button
              type="button"
              onClick={() => setIsExpanded((prev) => !prev)}
              aria-expanded={isExpanded}
              aria-controls="reading-passage-region"
              className="text-xs font-medium text-primary hover:underline flex items-center gap-1 focus:outline-none focus-visible:ring-2 focus-visible:ring-primary rounded px-1.5 py-0.5"
            >
              {isExpanded ? "Hide passage" : "Show passage"}
              {isExpanded ? <ChevronUp size={14} /> : <ChevronDown size={14} />}
            </button>
          </div>
          {isExpanded && (
            <div className="max-h-[50vh] overflow-y-auto">
              {renderPassageBody()}
            </div>
          )}
        </div>
        <div>{children}</div>
      </div>
    );
  }

  // Single question mode (taking exam, practice, quiz)
  return (
    <div className={`w-full ${className}`.trim()}>
      {/* Mobile view (< 1024px): Sticky "Passage" bar above the question that expands/collapses inline */}
      <div className="block lg:hidden mb-4">
        <div className="bg-card border border-border rounded-xl shadow-xs overflow-hidden sticky top-16 z-20">
          <div className="bg-muted/40 px-4 py-2.5 flex items-center justify-between border-b border-border">
            <div className="flex items-center gap-2 truncate pr-2">
              <BookOpen size={16} className="text-primary shrink-0" />
              <span className="text-xs font-bold uppercase tracking-wider text-muted-foreground">
                Passage
              </span>
              {passageTitle && (
                <span className="text-xs font-semibold text-foreground truncate">
                  · {passageTitle}
                </span>
              )}
            </div>
            <button
              type="button"
              onClick={() => setIsExpanded((prev) => !prev)}
              aria-expanded={isExpanded}
              aria-controls="reading-passage-region"
              className="text-xs font-medium text-primary hover:underline flex items-center gap-1 shrink-0 focus:outline-none focus-visible:ring-2 focus-visible:ring-primary rounded px-2 py-1"
            >
              <span>{isExpanded ? "Hide passage" : "Show passage"}</span>
              {isExpanded ? <ChevronUp size={14} /> : <ChevronDown size={14} />}
            </button>
          </div>

          {isExpanded && (
            <div className="max-h-[60vh] overflow-y-auto border-t border-border/50 bg-background/50">
              {renderPassageBody()}
            </div>
          )}
        </div>
      </div>

      {/* Desktop view (1024px and up): Two columns, passage on left (sticky scroll area), question on right */}
      <div className="hidden lg:grid lg:grid-cols-12 lg:gap-6 lg:items-start">
        {/* Left Column: Sticky Passage Panel */}
        <aside className="lg:col-span-6 xl:col-span-5 sticky top-20 max-h-[calc(100vh-6rem)] flex flex-col bg-card border border-border rounded-xl shadow-xs overflow-hidden">
          <div className="bg-muted/40 px-5 py-3 border-b border-border flex items-center justify-between">
            <div className="flex items-center gap-2">
              <BookOpen size={17} className="text-primary" />
              <span className="text-xs font-bold uppercase tracking-wider text-muted-foreground">
                Passage
              </span>
            </div>
          </div>
          <div className="overflow-y-auto flex-1">
            {renderPassageBody()}
          </div>
        </aside>

        {/* Right Column: Question Content */}
        <main className="lg:col-span-6 xl:col-span-7">
          {children}
        </main>
      </div>

      {/* Mobile Question content */}
      <div className="block lg:hidden">
        {children}
      </div>
    </div>
  );
}
